import { useState } from "react";
import { observer } from "mobx-react-lite";
import { SparklesIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { Button, ButtonIntent } from "src/components/Button";
import { ExportDesignWizard } from "src/components/ExportDesignWizard/ExportDesignWizard";
import { useActiveParams } from "src/hooks/useActiveParams";
import { useFlagIdentity } from "src/contexts/posthog.context";
import type { Editor } from "src/types/Editor";

// ExportDesignButton is the glowing purple paywall CTA. Only visible once the
// current world has at least one building (world.hasBuilding) — the user can
// only export something once a design exists in the editor. We use
// world.hasBuilding rather than DesignsGenerator.list.length because the
// latter only tracks current-session generations; loading a saved design
// populates the world directly without going through the generator buffer.
// The glow draws attention to the newly-unlocked action without being modal.
export const ExportDesignButton = observer(function ExportDesignButton(props: {
  editor: Editor;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { projectId, scenarioId, designId } = useActiveParams();
  const { capture } = useFlagIdentity();
  const world = props.editor.world;
  if (!world || !world.hasBuilding) return null;

  const open = () => {
    capture("export_wizard_opened", {
      projectId,
      scenarioId,
      designId,
      buildingsCount: world.buildings.length,
    });
    setIsOpen(true);
  };

  return (
    <>
      <div
        className={classNames(
          "rounded",
          // Subtle purple glow + slow pulse. Disabled by `motion-reduce`
          // utilities for accessibility — users who opted out of animations
          // still see the colour but not the pulse.
          "shadow-[0_0_12px_2px_rgba(168,85,247,0.6)]",
          "motion-safe:animate-pulse",
        )}
      >
        <Button
          intent={ButtonIntent.PRIMARY}
          Icon={SparklesIcon}
          title="Export Design"
          onClick={open}
        />
      </div>
      <ExportDesignWizard
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        world={world}
        mapState={props.editor.mapState}
        projectId={projectId}
        scenarioId={scenarioId}
        designId={designId}
      />
    </>
  );
});
